export type EffortLevel = 'low' | 'medium' | 'high';

export interface AgentConfigSnapshot {
  systemPrompt: string;
  model: string;
  temperature: number;
  maxTokens: number;
  responseMode: string;
  recommendedModels: string[];
  effort: EffortLevel;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

export interface ProposedConfig {
  systemPrompt?: string;
  model?: string;
  temperature?: number;
  maxTokens?: number;
  responseMode?: string;
  recommendedModels?: string[];
  dataSources?: string[];
  fleet?: string[];
  agentType?: string;
  orchestrationMode?: string;
  effort?: EffortLevel;
  agentName?: string;
  description?: string;
  llmProvider?: string;
  visibility?: string;
  tags?: string[];
}

export interface BuilderPayload {
  stage?: string;
  message?: string;
  suggestions?: string[];
  agentConfig?: Record<string, unknown>;
}

export const EMPTY_AGENT_CONFIG: AgentConfigSnapshot = {
  systemPrompt: '',
  model: '',
  temperature: 0.7,
  maxTokens: 2000,
  responseMode: 'sync',
  recommendedModels: [],
  effort: 'medium',
};

function asEffort(value: unknown): EffortLevel | undefined {
  return value === 'low' || value === 'medium' || value === 'high'
    ? value
    : undefined;
}

export function parseBuilderResponse(raw: string): BuilderPayload {
  const trimmed = (raw || '').trim();
  if (!trimmed) return { message: '' };

  const tryParse = (text: string): BuilderPayload | null => {
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      return {
        stage: parsed.stage ? String(parsed.stage) : undefined,
        message: parsed.message
          ? String(parsed.message)
          : typeof parsed === 'object'
            ? undefined
            : text,
        suggestions: Array.isArray(parsed.suggestions)
          ? parsed.suggestions.map((s) => String(s))
          : undefined,
        agentConfig:
          parsed.agent_config && typeof parsed.agent_config === 'object'
            ? (parsed.agent_config as Record<string, unknown>)
            : parsed.agentConfig && typeof parsed.agentConfig === 'object'
              ? (parsed.agentConfig as Record<string, unknown>)
              : undefined,
      };
    } catch {
      return null;
    }
  };

  const direct = tryParse(trimmed);
  if (direct) {
    return {
      ...direct,
      message: direct.message || direct.agentConfig ? direct.message : trimmed,
    };
  }

  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence?.[1]) {
    const fromFence = tryParse(fence[1].trim());
    if (fromFence) {
      return {
        ...fromFence,
        message:
          fromFence.message ||
          trimmed.replace(fence[0], '').trim() ||
          fromFence.message,
      };
    }
  }

  const brace = trimmed.indexOf('{');
  const lastBrace = trimmed.lastIndexOf('}');
  if (brace >= 0 && lastBrace > brace) {
    const fromSlice = tryParse(trimmed.slice(brace, lastBrace + 1));
    if (fromSlice) {
      return {
        ...fromSlice,
        message:
          fromSlice.message ||
          trimmed.slice(0, brace).trim() ||
          fromSlice.message,
      };
    }
  }

  return { message: trimmed };
}

export function mapDataSourceNames(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object') {
        const o = entry as Record<string, unknown>;
        return String(o.name ?? o.data_source_id ?? o.dataSourceId ?? '');
      }
      return '';
    })
    .filter(Boolean);
}

function cleanBuilderText(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  const text = String(value).replace(/\u0000/g, '');
  return text || undefined;
}

export function toProposed(agentConfig: Record<string, unknown>): ProposedConfig {
  const settings =
    agentConfig.settings && typeof agentConfig.settings === 'object'
      ? (agentConfig.settings as Record<string, unknown>)
      : {};
  const fleet =
    agentConfig.fleet && typeof agentConfig.fleet === 'object'
      ? (agentConfig.fleet as Record<string, unknown>)
      : {};
  const orch =
    agentConfig.orchestration && typeof agentConfig.orchestration === 'object'
      ? (agentConfig.orchestration as Record<string, unknown>)
      : {};
  const fleetAgents = Array.isArray(fleet.agents)
    ? fleet.agents.map((a) => String(a))
    : undefined;
  const recommended =
    agentConfig.recommended_models ??
    agentConfig.recommendedModels ??
    settings.recommended_models;
  const recommendedModels = Array.isArray(recommended)
    ? recommended
        .map((model) => String(model).replace(/\u0000/g, '').trim())
        .filter(Boolean)
    : undefined;
  const dataSources =
    mapDataSourceNames(agentConfig.data_sources) ??
    mapDataSourceNames(agentConfig.dataSources);
  const tags = Array.isArray(agentConfig.tags)
    ? agentConfig.tags.map((t) => String(t))
    : undefined;

  return {
    systemPrompt: cleanBuilderText(
      agentConfig.system_prompt ?? agentConfig.systemPrompt,
    ),
    model: agentConfig.llm_model
      ? String(agentConfig.llm_model)
      : agentConfig.model
        ? String(agentConfig.model)
        : undefined,
    temperature:
      typeof settings.temperature === 'number'
        ? settings.temperature
        : typeof agentConfig.temperature === 'number'
          ? agentConfig.temperature
          : undefined,
    maxTokens:
      typeof settings.max_tokens === 'number'
        ? settings.max_tokens
        : typeof agentConfig.max_tokens === 'number'
          ? agentConfig.max_tokens
          : typeof agentConfig.maxTokens === 'number'
            ? agentConfig.maxTokens
            : undefined,
    responseMode: settings.response_mode
      ? String(settings.response_mode)
      : agentConfig.response_mode
        ? String(agentConfig.response_mode)
        : agentConfig.responseMode
          ? String(agentConfig.responseMode)
          : undefined,
    recommendedModels:
      recommendedModels && recommendedModels.length
        ? recommendedModels
        : undefined,
    dataSources,
    fleet: fleetAgents,
    agentType: agentConfig.agent_type
      ? String(agentConfig.agent_type)
      : agentConfig.agentType
        ? String(agentConfig.agentType)
        : undefined,
    orchestrationMode: orch.mode ? String(orch.mode) : undefined,
    effort:
      asEffort(settings.effort) ??
      asEffort(agentConfig.effort),
    agentName: agentConfig.agent_name
      ? String(agentConfig.agent_name)
      : agentConfig.agentName
        ? String(agentConfig.agentName)
        : undefined,
    description: agentConfig.description
      ? String(agentConfig.description)
      : undefined,
    llmProvider: agentConfig.llm_provider
      ? String(agentConfig.llm_provider)
      : agentConfig.llmProvider
        ? String(agentConfig.llmProvider)
        : undefined,
    visibility: agentConfig.visibility
      ? String(agentConfig.visibility)
      : undefined,
    tags,
  };
}

export function configsDiffer(
  current: AgentConfigSnapshot,
  fleetNames: string[],
  dataSourceNames: string[],
  proposed: ProposedConfig,
): boolean {
  if (
    proposed.systemPrompt != null &&
    proposed.systemPrompt !== current.systemPrompt
  ) {
    return true;
  }
  if (proposed.model != null && proposed.model !== current.model) return true;
  if (
    proposed.temperature != null &&
    proposed.temperature !== current.temperature
  ) {
    return true;
  }
  if (proposed.maxTokens != null && proposed.maxTokens !== current.maxTokens) {
    return true;
  }
  if (
    proposed.responseMode != null &&
    proposed.responseMode !== current.responseMode
  ) {
    return true;
  }
  if (proposed.effort != null && proposed.effort !== current.effort) {
    return true;
  }
  if (proposed.agentName) return true;
  if (proposed.recommendedModels) {
    const a = [...proposed.recommendedModels].sort().join(',');
    const b = [...current.recommendedModels].sort().join(',');
    if (a !== b) return true;
  }
  if (proposed.dataSources) {
    const a = [...proposed.dataSources].sort().join(',');
    const b = [...dataSourceNames].sort().join(',');
    if (a !== b) return true;
  }
  if (proposed.fleet) {
    const a = [...proposed.fleet].sort().join(',');
    const b = [...fleetNames].sort().join(',');
    if (a !== b) return true;
  }
  return false;
}

export const INTEGRATION_DRAG_TYPE = 'application/x-lumina-integration';

export interface BuilderAttachment {
  kind: 'data_source' | 'mcp';
  name: string;
}

export function attachmentKey(ref: BuilderAttachment): string {
  return `${ref.kind}:${ref.name}`;
}

export function dedupeAttachments(
  refs: BuilderAttachment[],
): BuilderAttachment[] {
  const seen = new Set<string>();
  const out: BuilderAttachment[] = [];
  for (const ref of refs) {
    const name = ref.name.trim();
    if (!name) continue;
    const key = `${ref.kind}:${name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ kind: ref.kind, name });
  }
  return out;
}

export function attachmentToken(ref: BuilderAttachment): string {
  return ref.kind === 'mcp' ? `$Mcp:${ref.name}` : `$DataSource:${ref.name}`;
}

export function parseIntegrationDrop(
  dataTransfer: DataTransfer,
): BuilderAttachment | null {
  const typed = dataTransfer.getData(INTEGRATION_DRAG_TYPE);
  if (typed) {
    try {
      const parsed = JSON.parse(typed) as { kind?: string; name?: string };
      if (
        parsed &&
        typeof parsed === 'object' &&
        (parsed.kind === 'data_source' || parsed.kind === 'mcp') &&
        typeof parsed.name === 'string' &&
        parsed.name.trim()
      ) {
        return { kind: parsed.kind, name: parsed.name.trim() };
      }
    } catch {
      // Configure-tab chips set text/plain only.
    }
  }
  const plain = (dataTransfer.getData('text/plain') || '').trim();
  if (!plain) return null;
  return { kind: 'data_source', name: plain };
}

function nameList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (typeof entry === 'string') return entry.trim();
      if (entry && typeof entry === 'object') {
        const row = entry as Record<string, unknown>;
        return String(
          row.name ??
            row.mcp_id ??
            row.mcpId ??
            row.data_source_id ??
            row.dataSourceId ??
            '',
        ).trim();
      }
      return '';
    })
    .filter(Boolean);
}

export function mergeDroppedRefs(
  agentConfig: Record<string, unknown>,
  refs: BuilderAttachment[],
): Record<string, unknown> {
  const dropped = dedupeAttachments(refs);
  if (!dropped.length) return agentConfig;
  const next: Record<string, unknown> = { ...agentConfig };
  const dataSources = new Set(
    nameList(next.data_sources).concat(nameList(next.dataSources)),
  );
  const mcpServers = new Set(
    nameList(next.mcp_servers).concat(nameList(next.mcpServers)),
  );
  for (const ref of dropped) {
    if (ref.kind === 'mcp') mcpServers.add(ref.name);
    else dataSources.add(ref.name);
  }
  if (dataSources.size) next.data_sources = [...dataSources];
  if (mcpServers.size) next.mcp_servers = [...mcpServers];
  return next;
}
